import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ClipboardList, FilePlus2 } from 'lucide-react';
import toast from 'react-hot-toast';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import FormModalLayout from '../../components/Common/FormModalLayout';
import { getAppHomePath } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { fiscalizacaoService, identityService, licenseService, processService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import ConsultaPublicaSearch from '../../components/Public/ConsultaPublicaSearch';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import { fetchAllPages } from '../../utils/apiPagination';
import { isPortalOwned, resolvePortalScope } from '../../utils/portalScope';
import { requerimentoStatusMeta } from '../../utils/requerimentoStatus';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

const PortalRequerente = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showProtocolo, setShowProtocolo] = useState(false);
  const [protocolo, setProtocolo] = useState({ processo_id: '', assunto: '', arquivo: '', arquivo_base64: '', filename: '' });
  const [evidencia, setEvidencia] = useState({ cond: null, file: null });
  const [exigencia, setExigencia] = useState({ row: null, texto: '' });
  const [defesa, setDefesa] = useState({ auto: null, texto: '', arquivo_base64: '', filename: '' });

  const { data: licenses = [], isLoading, isError: licError, error: licErr } = useQuery(
    ['portal-licencas'],
    () => fetchAllPages((params) => licenseService.getLicencas(params))
  );
  const { data: processes = [], isError: procError, error: procErr } = useQuery(
    ['portal-processos'],
    () => fetchAllPages((params) => processService.getProcessos(params))
  );
  const { data: clients = [], isError: cliError, error: cliErr } = useQuery(
    ['portal-clients'],
    () => fetchAllPages((params) => identityService.getClients(params))
  );
  const { data: requerimentos = [] } = useQuery(['portal-requerimentos'], () =>
    processService.listRequerimentos()
  );
  const { data: pedidos = [] } = useQuery(['portal-pedidos'], () => processService.listPedidos());

  const scope = useMemo(() => resolvePortalScope(user, clients), [user, clients]);
  const mine = useMemo(() => licenses.filter((row) => isPortalOwned(row, scope)), [licenses, scope]);
  const mineProcesses = useMemo(
    () => processes.filter((row) => isPortalOwned(row, scope)),
    [processes, scope]
  );

  const mineIds = useMemo(() => new Set(mine.map((row) => row.id)), [mine]);
  const { data: condRaw, isError: condError } = useQuery(
    ['portal-condicionantes', [...mineIds].join(',')],
    async () => {
      const lists = await Promise.all(
        [...mineIds].map((id) =>
          licenseService.getCondicionantesByLicenca(id).then((r) => unwrap(r.data))
        )
      );
      return lists.flat();
    },
    { enabled: mineIds.size > 0 }
  );
  const pendingDocs = unwrap(condRaw).filter((row) => ['1', '2', '3', 1, 2, 3].includes(row.situacao));

  const pending = mine.filter((row) => ['1', '2', '4', 1, 2, 4].includes(row.status));
  const openProcesses = mineProcesses.filter((p) => [1, 2].includes(p.situacao));
  const mineEmpIds = useMemo(
    () => new Set(mine.map((row) => row.empreendimento_id).filter(Boolean)),
    [mine]
  );
  const mineProcIds = useMemo(() => new Set(mineProcesses.map((row) => row.id)), [mineProcesses]);

  const { data: planosRaw } = useQuery(['portal-planos'], () =>
    licenseService.listPlanosRegularizacao().then((r) => unwrap(r.data))
  );
  const meusPlanos = useMemo(
    () => unwrap(planosRaw).filter((p) => p.empreendimento_id && mineEmpIds.has(p.empreendimento_id)),
    [planosRaw, mineEmpIds]
  );

  const { data: exigRaw } = useQuery(
    ['portal-exigencias', [...mineProcIds].join(',')],
    async () => {
      const lists = await Promise.all(
        [...mineProcIds].map((id) =>
          processService.getTramitacoesByProcesso(id).then((r) => unwrap(r.data))
        )
      );
      return lists.flat().filter((row) => row.is_exigencia || row.exigencia);
    },
    { enabled: mineProcIds.size > 0 }
  );
  const exigencias = unwrap(exigRaw);

  const { data: autosRaw } = useQuery(['portal-autos'], () =>
    fiscalizacaoService.listAutos({ limit: 500 }).then((r) => unwrap(r.data))
  );
  const meusAutos = useMemo(
    () => unwrap(autosRaw).filter((row) => row.processo_id && mineProcIds.has(row.processo_id)),
    [autosRaw, mineProcIds]
  );

  const { data: taxasRaw } = useQuery(['portal-taxas'], () =>
    processService.listTaxas({ limit: 500 }).then((r) => unwrap(r.data))
  );
  const minhasTaxas = useMemo(
    () => unwrap(taxasRaw).filter((row) => row.processo_id && mineProcIds.has(row.processo_id)),
    [taxasRaw, mineProcIds]
  );

  const sendDefesa = useMutation(
    async ({ auto, texto, arquivo_base64, filename }) => {
      if (!texto?.trim() || !arquivo_base64) {
        throw new Error('Informe o texto e anexe a peça de defesa.');
      }
      await processService.createProtocolo({
        processo_id: auto.processo_id,
        assunto: `Defesa do auto ${auto.numero || auto.id}`,
        arquivo_base64,
        filename: filename || 'defesa.pdf',
      });
      await fiscalizacaoService.updateAuto(auto.id, {
        status: 'defesa_apresentada',
        defesa_texto: texto.trim(),
      });
    },
    {
      onSuccess: () => {
        toast.success('Defesa protocolada no processo.');
        setDefesa({ auto: null, texto: '', arquivo_base64: '', filename: '' });
        queryClient.invalidateQueries(['portal-autos']);
        queryClient.invalidateQueries(['portal-processos']);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const sendExigencia = useMutation(
    ({ row, texto }) => processService.responderExigencia(row.id, { texto }),
    {
      onSuccess: () => {
        toast.success('Resposta da exigência enviada.');
        setExigencia({ row: null, texto: '' });
        queryClient.invalidateQueries(['portal-exigencias']);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const sendProtocolo = useMutation((payload) => processService.createProtocolo(payload), {
    onSuccess: () => {
      toast.success('Petição protocolada. O órgão receberá na tramitação.');
      setShowProtocolo(false);
      setProtocolo({ processo_id: '', assunto: '', arquivo: '', arquivo_base64: '', filename: '' });
      queryClient.invalidateQueries(['portal-processos']);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });

  const sendEvidencia = useMutation(
    async ({ cond, file }) => {
      await licenseService.uploadCondicionanteAnexo(cond.id, file);
      await licenseService.updateCondicionante(cond.id, { situacao: '3' });
    },
    {
      onSuccess: () => {
        toast.success('Evidência enviada. O analista revisará o cumprimento.');
        setEvidencia({ cond: null, file: null });
        queryClient.invalidateQueries(['portal-condicionantes']);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  return (
    <FormPageShell>
      <PageHeader
        title="Portal do requerente"
        description="Pendências, protocolo eletrônico e consulta de certidão deste órgão — não é SEI/DOU/IBAMA."
        breadcrumbs={[
          { label: 'Início', to: getAppHomePath() },
          { label: 'Portal' },
        ]}
      >
        <Link to="/pedidos" className="btn btn-primary inline-flex items-center gap-2">
          <FilePlus2 className="h-4 w-4" />
          Fazer um pedido
        </Link>
        <Link to="/requerimentos/novo" className="btn btn-outline inline-flex items-center gap-2">
          Requerer licenciamento
        </Link>
        <button type="button" className="btn btn-outline" onClick={() => setShowProtocolo(true)}>
          Protocolar petição
        </button>
      </PageHeader>
      {licError || procError || cliError || condError ? (
        <div className="mt-4 space-y-2">
          {licError ? <QueryErrorBanner error={licErr} /> : null}
          {procError ? <QueryErrorBanner error={procErr} /> : null}
          {cliError ? <QueryErrorBanner error={cliErr} /> : null}
          {condError ? <QueryErrorBanner error={new Error('Falha ao carregar condicionantes.')} /> : null}
        </div>
      ) : null}
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs uppercase text-gray-500">Licenças minhas</p>
          <p className="text-2xl font-semibold">{mine.length}</p>
          <p className="mt-1 text-xs text-gray-500">Consulta do cidadão / empresa</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs uppercase text-amber-800">Pendências</p>
          <p className="text-2xl font-semibold text-amber-900">{pending.length}</p>
          <p className="mt-1 text-xs text-amber-800">Licenças em análise ou prorrogação</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs uppercase text-gray-500">Condicionantes a cumprir</p>
          <p className="text-2xl font-semibold">{pendingDocs.length}</p>
          <p className="mt-1 text-xs text-gray-500">Anexar evidências obrigatórias</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs uppercase text-gray-500">Requerimentos</p>
          <p className="text-2xl font-semibold">{unwrap(requerimentos).length}</p>
          <p className="mt-1 text-xs text-gray-500">{openProcesses.length} processo(s) em aberto</p>
        </div>
      </div>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="flex items-center justify-between border-b px-6 py-3">
          <div>
            <div className="text-sm font-semibold">Meus pedidos</div>
            <p className="text-xs text-slate-500">Solicitação diversa. O órgão recebe um processo Diversos já vinculado a você.</p>
          </div>
          <Link to="/pedidos" className="text-sm font-medium text-primary-700">
            Novo pedido
          </Link>
        </div>
        {unwrap(pedidos).length === 0 ? (
          <p className="px-6 py-8 text-gray-500">Nenhum pedido. Use “Fazer um pedido” para descrever o que precisa e anexar documentos.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {unwrap(pedidos).slice(0, 5).map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 px-6 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{row.numero}</p>
                  <p className="truncate text-sm text-slate-600">{row.texto}</p>
                </div>
                <Link to="/pedidos" className="shrink-0 text-sm font-medium text-primary-700">
                  Abrir
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="flex items-center justify-between border-b px-6 py-3">
          <div>
            <div className="text-sm font-semibold">Meus requerimentos</div>
            <p className="text-xs text-slate-500">Rascunho até o órgão analisar e abrir o processo.</p>
          </div>
          <Link to="/requerimentos" className="text-sm font-medium text-primary-700">
            Ver todos
          </Link>
        </div>
        {unwrap(requerimentos).length === 0 ? (
          <p className="px-6 py-8 text-gray-500">Nenhum requerimento. Use “Requerer licenciamento” para abrir um rascunho.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {unwrap(requerimentos).slice(0, 5).map((row) => {
              const meta = requerimentoStatusMeta(row.status);
              return (
                <li key={row.id} className="flex items-center justify-between px-6 py-3">
                  <div>
                    <p className="font-medium text-slate-900">{row.numero}</p>
                    <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${meta.className}`}>
                      {meta.label}
                    </span>
                  </div>
                  <Link to={`/requerimentos/${row.id}`} className="text-sm font-medium text-primary-700">
                    {row.editavel ? 'Continuar' : 'Abrir'}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="border-b px-6 py-3 text-sm font-semibold">Minhas pendências</div>
        {isLoading ? (
          <p className="px-6 py-8 text-gray-500">Carregando...</p>
        ) : pending.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">
            <ClipboardList className="mb-2 inline h-5 w-5" /> Nenhuma pendência. Protocolar uma petição é a próxima ação se houver exigência.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Número</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {pending.map((row) => (
                <tr key={row.id}>
                  <td className="px-6 py-4 text-sm font-medium">{row.numero}</td>
                  <td className="px-6 py-4 text-sm">{row.status}</td>
                  <td className="px-6 py-4 text-right text-sm">
                    <Link to={`/licenses?highlight=${row.id}`} className="text-primary-700">
                      Abrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="border-b px-6 py-3 text-sm font-semibold">Condicionantes a cumprir</div>
        {condError ? (
          <p className="px-6 py-8 text-red-700">Não foi possível carregar as condicionantes.</p>
        ) : pendingDocs.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">Nenhuma condicionante pendente nas suas licenças.</p>
        ) : (
          <ul className="divide-y">
            {pendingDocs.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 px-6 py-3 text-sm">
                <span>
                  {row.condicionante || row.descricao || row.id}
                  <span className="ml-2 text-xs text-gray-500">
                    {['3', 3].includes(row.situacao) ? 'Em atendimento' : 'Pendente'}
                  </span>
                </span>
                <button type="button" className="text-primary-700" onClick={() => setEvidencia({ cond: row, file: null })}>
                  Enviar evidência
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="border-b px-6 py-3 text-sm font-semibold">Ações de regularização</div>
        {meusPlanos.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">Nenhum plano de regularização nos seus empreendimentos.</p>
        ) : (
          <ul className="divide-y">
            {meusPlanos.map((row) => (
              <li key={row.id} className="px-6 py-3 text-sm">
                {row.titulo || row.descricao || row.id}
                {row.status ? <span className="ml-2 text-xs text-gray-500">{row.status}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="border-b px-6 py-3 text-sm font-semibold">Exigências formais</div>
        {exigencias.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">Nenhuma exigência formal nos seus processos.</p>
        ) : (
          <ul className="divide-y">
            {exigencias.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 px-6 py-3 text-sm">
                <span>
                  {row.parecer || 'Exigência'}
                  <span className="ml-2 text-xs text-gray-500">
                    {row.exigencia_status || 'aberta'}
                    {row.prazo_dias ? ` · ${row.prazo_dias} dia(s)` : ''}
                  </span>
                  {row.resposta_texto ? (
                    <p className="mt-1 text-xs text-gray-600">Sua resposta: {row.resposta_texto}</p>
                  ) : null}
                  {row.analise_texto ? (
                    <p className="mt-1 text-xs text-gray-600">Análise: {row.analise_texto}</p>
                  ) : null}
                </span>
                {!row.exigencia_status || row.exigencia_status === 'aberta' ? (
                  <button type="button" className="text-primary-700" onClick={() => setExigencia({ row, texto: '' })}>
                    Responder
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="border-b px-6 py-3 text-sm font-semibold">Autos e defesa</div>
        {meusAutos.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">Nenhum auto vinculado aos seus processos.</p>
        ) : (
          <ul className="divide-y">
            {meusAutos.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 px-6 py-3 text-sm">
                <span>
                  Auto {row.numero || row.id}
                  <span className="ml-2 text-xs text-gray-500">{row.status}</span>
                  {row.prazo_defesa ? (
                    <span className="ml-2 text-xs text-gray-500">prazo {String(row.prazo_defesa).slice(0, 10)}</span>
                  ) : null}
                </span>
                {['lavrado', 'notificado', 'aguardando_defesa'].includes(row.status) ? (
                  <button
                    type="button"
                    className="text-primary-700"
                    onClick={() => setDefesa({ auto: row, texto: '', arquivo_base64: '', filename: '' })}
                  >
                    Protocolar defesa
                  </button>
                ) : null}
                {row.decisao ? (
                  <span className="text-xs text-gray-500">decisão: {row.decisao}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="data-list-card mt-6 overflow-hidden">
        <div className="border-b px-6 py-3 text-sm font-semibold">Taxas</div>
        {minhasTaxas.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">Nenhuma taxa emitida nos seus processos.</p>
        ) : (
          <ul className="divide-y">
            {minhasTaxas.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 px-6 py-3 text-sm">
                <span>
                  {row.descricao} · R$ {Number(row.valor || 0).toFixed(2)}
                  <span className="ml-2 text-xs text-gray-500">{row.status}</span>
                </span>
                {row.status === 'paga' ? (
                  <span className="text-xs text-emerald-700">Paga</span>
                ) : row.invoice_url ? (
                  <a href={row.invoice_url} target="_blank" rel="noreferrer" className="text-primary-700">
                    Pagar
                  </a>
                ) : (
                  <span className="text-xs text-gray-400">O órgão ainda não publicou o boleto</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-6">
        <ConsultaPublicaSearch
          title="Consulta pública de andamento"
          description={
            <>
              Também disponível sem login em{' '}
              <Link to="/process-lookup" className="text-primary-700">
                /process-lookup
              </Link>
              .
            </>
          }
          placeholder="Número do protocolo"
          entityLabel="processo"
          search={async (numero) => {
            const { data } = await processService.consultarProcesso(numero);
            return data;
          }}
          renderItem={(row) => (
            <>
              <strong>{row.numero}</strong> · {row.situacao_label || row.situacao}
            </>
          )}
        />
      </section>

      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-6">
        <ConsultaPublicaSearch
          title="Consulta pública de certidão"
          placeholder="Número da certidão de viabilidade"
          entityLabel="certidão"
          search={async (numero) => {
            const { data } = await licenseService.consultarCertidao(numero);
            return data;
          }}
          renderItem={(row) => (
            <>
              {row.numero_viabilidade || row.id} · processo {row.processo_id || '—'}
            </>
          )}
        />
      </section>

      {showProtocolo && (
        <FormModalLayout
          title="Protocolo eletrônico"
          subtitle="A petição entra no processo sem exibir a caixa interna do órgão."
          onClose={() => setShowProtocolo(false)}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!protocolo.processo_id || !protocolo.assunto.trim() || sendProtocolo.isLoading}
              onClick={() =>
                sendProtocolo.mutate({
                  processo_id: protocolo.processo_id,
                  assunto: protocolo.assunto.trim(),
                  ...(protocolo.arquivo_base64
                    ? {
                        arquivo_base64: protocolo.arquivo_base64,
                        filename: protocolo.filename || 'peticao.pdf',
                      }
                    : {}),
                })
              }
            >
              Protocolar
            </button>
          }
        >
          <div className="form-stack">
          <label className="form-label">Processo</label>
          <select
            className="form-select"
            value={protocolo.processo_id}
            onChange={(e) => setProtocolo((f) => ({ ...f, processo_id: e.target.value }))}
          >
            <option value="">Selecione</option>
            {openProcesses.concat(mineProcesses.filter((p) => !openProcesses.includes(p))).map((p) => (
              <option key={p.id} value={p.id}>
                {p.numero}
              </option>
            ))}
          </select>
          <label className="form-label mt-3">Assunto</label>
          <input className="form-input" value={protocolo.assunto} onChange={(e) => setProtocolo((f) => ({ ...f, assunto: e.target.value }))} />
          <label className="form-label mt-3">Anexo (PDF)</label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="form-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () =>
                setProtocolo((f) => ({
                  ...f,
                  arquivo: file.name,
                  filename: file.name,
                  arquivo_base64: String(reader.result || ''),
                }));
              reader.readAsDataURL(file);
            }}
          />
          {protocolo.filename ? <p className="mt-1 text-xs text-gray-500">{protocolo.filename}</p> : null}
          </div>
        </FormModalLayout>
      )}

      {evidencia.cond ? (
        <FormModalLayout
          title="Evidência de cumprimento"
          subtitle="O arquivo entra na condicionante. O analista aprova ou devolve — não é protocolo SEI."
          onClose={() => setEvidencia({ cond: null, file: null })}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!evidencia.file || sendEvidencia.isLoading}
              onClick={() => sendEvidencia.mutate(evidencia)}
            >
              Enviar evidência
            </button>
          }
        >
          <p className="text-sm text-gray-700">{evidencia.cond.condicionante || evidencia.cond.descricao}</p>
          <label className="form-label mt-3">Arquivo (PDF, imagem ou ofício)</label>
          <input
            type="file"
            className="form-input"
            onChange={(e) => setEvidencia((f) => ({ ...f, file: e.target.files?.[0] || null }))}
          />
          {evidencia.file ? <p className="mt-1 text-xs text-gray-500">{evidencia.file.name}</p> : null}
        </FormModalLayout>
      ) : null}

      {exigencia.row ? (
        <FormModalLayout
          title="Responder exigência"
          subtitle="A resposta fica vinculada à tramitação. O analista deferirá ou indeferirá."
          onClose={() => setExigencia({ row: null, texto: '' })}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!exigencia.texto.trim() || sendExigencia.isLoading}
              onClick={() => sendExigencia.mutate(exigencia)}
            >
              Enviar resposta
            </button>
          }
        >
          <p className="text-sm text-gray-700">{exigencia.row.parecer || 'Exigência formal'}</p>
          <label className="form-label mt-3">Resposta</label>
          <textarea
            className="form-input"
            rows={5}
            value={exigencia.texto}
            onChange={(e) => setExigencia((f) => ({ ...f, texto: e.target.value }))}
          />
        </FormModalLayout>
      ) : null}

      {defesa.auto ? (
        <FormModalLayout
          title="Protocolar defesa"
          subtitle="Texto e peça em PDF. Sem a peça o protocolo não entra."
          onClose={() => setDefesa({ auto: null, texto: '', arquivo_base64: '', filename: '' })}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!defesa.texto.trim() || !defesa.arquivo_base64 || sendDefesa.isLoading}
              onClick={() => sendDefesa.mutate(defesa)}
            >
              Protocolar defesa
            </button>
          }
        >
          <p className="text-sm text-gray-700">Auto {defesa.auto.numero || defesa.auto.id}</p>
          <label className="form-label mt-3">Razões da defesa</label>
          <textarea
            className="form-input"
            rows={5}
            value={defesa.texto}
            onChange={(e) => setDefesa((f) => ({ ...f, texto: e.target.value }))}
          />
          <label className="form-label mt-3">Peça (PDF)</label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="form-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () =>
                setDefesa((f) => ({
                  ...f,
                  filename: file.name,
                  arquivo_base64: String(reader.result || ''),
                }));
              reader.readAsDataURL(file);
            }}
          />
          {defesa.filename ? <p className="mt-1 text-xs text-gray-500">{defesa.filename}</p> : null}
        </FormModalLayout>
      ) : null}
    </FormPageShell>
  );
};

export default PortalRequerente;
